import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Text } from "@amzn/stencil-react-components/text";
import ListModalButton from "common/components/ListModal/ListModalButton";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { ApplicableList, ListModel, Visibility } from "lib/models/list-models";
import { AmazonianInformation } from "lib/models/oui-models";
import { UserPermissions } from "lib/services";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useEffect } from "react";
import { useHistory } from "react-router-dom";

import jobFamiliesMapping from "../../../configuration/AIForMe/jobFamiliesMapping.json";
import { List } from "./List";

const ListsPage: FC = () => {
  const history = useHistory();
  const {
    myLists,
    sharedLists,
    sharedWithMeLists,
    loading,
    getMyLists,
    getSharedLists,
    getSharedWithMeLists,
    evaluateLists,
  } = useLists();
  const [recommendedLists, setRecommendedLists] = React.useState<ApplicableList[]>([]);
  const [loadingRecommended, setLoadingRecommended] = React.useState(false);
  const tabs = [
    getString("lists.tabs.myLists"),
    getString("lists.tabs.sharedLists"),
    getString("lists.tabs.sharedWithMeLists"),
    getString("lists.tabs.public"),
  ];

  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  const allowedDomain = window.location.href.includes("aiforme") ? "aiforme" : "software";

  useEffect(() => {
    if (toggleLists) {
      getMyLists({ includeItemDetails: true });
      getSharedLists({ includeItemDetails: true });
      getSharedWithMeLists({ includeItemDetails: true });
    }
  }, [toggleLists]);

  useEffect(() => {
    setRecommendedLists([]);
    const userInfo = UserInformation.current.getCachedAmazonianInformation() as AmazonianInformation;
    const jobFamily = (jobFamiliesMapping as { [key: string]: string })[userInfo?.jobInfo?.jobFamilyName];

    if (!jobFamily) return;

    setLoadingRecommended(true);
    evaluateLists({ jobFamilyName: [jobFamily] }, { includeItemDetails: true })
      .then((lists) => setRecommendedLists(lists))
      .finally(() => setLoadingRecommended(false));
  }, [allowedDomain]);

  if (!toggleLists) {
    history.push("/");
    return null;
  }

  const renderLists = (lists: ListModel[], listType: ListType, allowDeletion?: boolean, allowEdit?: boolean) => {
    return (
      <Col gridGap="S300">
        {loading && <Spinner />}
        {!loading &&
          lists.map(
            (list) =>
              list.tags.includes("favorite") &&
              list.allowedDomains.includes(allowedDomain) && (
                <List key={`Favorite-List`} listType={listType} {...list} />
              )
          )}
        {!loading &&
          lists.map(
            (list, index) =>
              list.allowedDomains.includes(allowedDomain) &&
              !list.tags.includes("favorite") && (
                <List
                  key={`List-${index}`}
                  listType={listType}
                  allowDeletion={allowDeletion}
                  allowEdit={allowEdit}
                  {...list}
                />
              )
          )}
        {!loading && lists.length == 0 && <Text fontSize="T200">{getString("lists.noLists")}</Text>}
      </Col>
    );
  };

  const renderPublicLists = () => {
    return (
      <Col gridGap="S300">
        {loadingRecommended && <Spinner />}
        {!loadingRecommended &&
          recommendedLists.length > 0 &&
          recommendedLists.map((list, index) => (
            <List listType={ListType.PUBLIC_LISTS} key={`public-list-${index}`} {...list} />
          ))}
        {!loadingRecommended && recommendedLists.length === 0 && (
          <Text fontSize="T200">{getString("lists.noLists")}</Text>
        )}
      </Col>
    );
  };

  return (
    <Col>
      <PageMainContainer>
        <Container paddingTop={32} paddingBottom={32}>
          <Col gridGap={32}>
            <Row justifyContent="space-between">
              <Text fontSize="T400" fontWeight="bold">
                {getString("lists.title")}
              </Text>
              <ListModalButton />
            </Row>
            <Text fontSize="T200">{getString("lists.description")}</Text>

            <TabBar {...tabBarProps} />
            <TabSwitcher {...tabSwitcherProps}>
              <TabPanel value={getString("lists.tabs.myLists")}>
                {renderLists(myLists, ListType.MY_LISTS, true, true)}
              </TabPanel>
              <TabPanel value={getString("lists.tabs.sharedLists")}>
                {renderLists(sharedLists, ListType.SHARED_LISTS, true, true)}
              </TabPanel>
              <TabPanel value={getString("lists.tabs.sharedWithMeLists")}>
                {renderLists(sharedWithMeLists, ListType.SHARED_WITH_ME_LISTS, false, false)}
              </TabPanel>
              <TabPanel value={getString("lists.tabs.public")}>{renderPublicLists()}</TabPanel>
            </TabSwitcher>
          </Col>
        </Container>
      </PageMainContainer>
    </Col>
  );
};

export default ListsPage;
